import type { Maybe } from "../types/storefront.types";
import { CartErrorCode, CustomerErrorCode } from "../types/storefront.types";

const CUSTOMER_ERROR_MESSAGES: Record<string, { en: string; jp: string; ko: string }> = {
  DEFAULT: {
    en: "An unexpected error occurred. Please try again in a moment.",
    jp: "一時的なエラーが発生しました。しばらくしてからもう一度お試しください。",
    ko: "일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
  },
  [CustomerErrorCode.AlreadyEnabled]: {
    en: "This email is already registered. Please sign in instead.",
    jp: "このメールアドレスは既に登録されています。ログインしてください。",
    ko: "이미 가입된 이메일 주소입니다. 로그인 페이지를 이용해 주세요.",
  },
  [CustomerErrorCode.BadDomain]: {
    en: "Please enter a valid email address.",
    jp: "無効なメールアドレスです。正しいアドレスを入力してください。",
    ko: "사용할 수 없는 이메일 주소입니다. 이메일 주소를 다시 확인해 주세요.",
  },
  [CustomerErrorCode.Blank]: {
    en: "This field cannot be left blank.",
    jp: "必須項目です。入力してください。",
    ko: "빈칸을 입력해 주세요. 필수 항목입니다.",
  },
  [CustomerErrorCode.ContainsHtmlTags]: {
    en: "HTML tags cannot be used in this field.",
    jp: "セキュリティのため、HTMLタグはご使用いただけません。",
    ko: "보안을 위해 HTML 태그는 입력할 수 없습니다.",
  },
  [CustomerErrorCode.ContainsUrl]: {
    en: "Web addresses (URLs) cannot be used in this field.",
    jp: "URL（ウェブサイトのアドレス）を入力することはできません。",
    ko: "URL(웹 사이트 주소)은 입력할 수 없습니다.",
  },
  [CustomerErrorCode.CustomerDisabled]: {
    en: "This account has been deactivated. Please contact customer support.",
    jp: "このアカウントは一時的に無効化されています。カスタマーサポートまでお問い合わせください。",
    ko: "사용이 비활성화된 계정입니다. 자세한 내용은 고객센터로 문의해 주세요.",
  },
  [CustomerErrorCode.Invalid]: {
    en: "The value you entered is incorrect. Please check and try again.",
    jp: "入力内容に誤りがあります。もう一度ご確認ください。",
    ko: "입력하신 값이 올바르지 않습니다. 다시 확인해 주세요.",
  },
  [CustomerErrorCode.InvalidMultipassRequest]: {
    en: "Your login session has expired. Please try signing in again.",
    jp: "ログインセッションの有効期限が切れました。再度ログインをお試しください。",
    ko: "로그인 인증 세션이 유효하지 않습니다. 다시 로그인을 진행해 주세요.",
  },
  [CustomerErrorCode.NotFound]: {
    en: "The address could not be found.",
    jp: "ご指定の住所が見つかりませんでした。再度ご確認ください。",
    ko: "등록되지 않은 주소입니다. 입력하신 주소를 다시 확인해 주세요.",
  },
  [CustomerErrorCode.PasswordStartsOrEndsWithWhitespace]: {
    en: "Passwords cannot start or end with a space.",
    jp: "パスワードの最初と最後にスペース（空白）を含めることはできません。",
    ko: "비밀번호의 시작과 끝에는 공백(띄어쓰기)을 사용할 수 없습니다.",
  },
  [CustomerErrorCode.Taken]: {
    en: "This information is already in use by another account.",
    jp: "この情報は既に別のアカウントで使用されています。",
    ko: "이미 사용 중인 정보입니다. 다른 내용을 입력해 주세요.",
  },
  [CustomerErrorCode.TokenInvalid]: {
    en: "The security token has expired or is invalid. Please try again.",
    jp: "認証トークンの有効期限が切れたか、無効です。もう一度やり直してください。",
    ko: "인증 정보가 만료되었거나 올바르지 않습니다. 다시 시도해 주세요.",
  },
  [CustomerErrorCode.TooLong]: {
    en: "The input is too long. Please shorten it and try again.",
    jp: "入力文字数が多すぎます。もう少し短く入力してください。",
    ko: "입력 가능한 글자 수를 초과했습니다. 조금 더 짧게 입력해 주세요.",
  },
  [CustomerErrorCode.TooShort]: {
    en: "The input is too short. Please write a bit more.",
    jp: "入力文字数が足りません。もう少し長く入力してください。",
    ko: "입력값이 너무 짧습니다. 조금 더 길게 작성해 주세요.",
  },
  [CustomerErrorCode.UnidentifiedCustomer]: {
    en: "Incorrect email or password. Please check your details and try again.",
    jp: "メールアドレスまたはパスワードが一致しません。再度ご確認ください。",
    ko: "이메일 또는 비밀번호가 일치하지 않습니다. 입력하신 내용을 확인해 주세요.",
  },
};

export const customerErrorHandler = (code: Maybe<CustomerErrorCode> | undefined, lang: "ko" | "en" | "jp"): string => {
  const safeCode = code && CUSTOMER_ERROR_MESSAGES[code] ? code : "DEFAULT";
  return CUSTOMER_ERROR_MESSAGES[safeCode][lang];
};

const CART_ERROR_MESSAGES: Record<string, { en: string; jp: string; ko: string }> = {
  DEFAULT: {
    en: "An unexpected error occurred. Please refresh the page and try again.",
    jp: "一時的なエラーが発生しました。ページを再読み込みしてもう一度お試しください。",
    ko: "일시적인 오류가 발생했습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.",
  },
  [CartErrorCode.AddressFieldContainsEmojis]: {
    en: "Please remove any emojis or special characters from the address.",
    jp: "住所に絵文字や特殊文字を入力することはできません。",
    ko: "주소에는 이모지(그림 문자)나 특수 문자를 입력할 수 없습니다.",
  },
  [CartErrorCode.AddressFieldContainsHtmlTags]: {
    en: "HTML tags cannot be used in the address field.",
    jp: "住所に入力できない文字が含まれています。",
    ko: "보안을 위해 주소에는 HTML 태그를 사용할 수 없습니다.",
  },
  [CartErrorCode.AddressFieldContainsUrl]: {
    en: "Web addresses (URLs) cannot be included in the address field.",
    jp: "住所にURL（ウェブサイトのアドレス）を入力することはできません。",
    ko: "주소에는 웹사이트 주소(URL)를 입력할 수 없습니다.",
  },
  [CartErrorCode.AddressFieldDoesNotMatchExpectedPattern]: {
    en: "The address format is incorrect. Please check your input.",
    jp: "住所の形式が正しくありません。入力内容をもう一度ご確認ください。",
    ko: "올바른 주소 형식이 아닙니다. 입력하신 내용을 다시 확인해 주세요.",
  },
  [CartErrorCode.AddressFieldIsRequired]: {
    en: "Please enter your address. This field is required.",
    jp: "住所は必須項目です。入力してください。",
    ko: "주소는 필수 입력 항목입니다. 주소를 입력해 주세요.",
  },
  [CartErrorCode.AddressFieldIsTooLong]: {
    en: "The address is too long. Please shorten it or use the second address line.",
    jp: "住所が長すぎます。お手数ですが、短くまとめて入力してください。",
    ko: "주소가 너무 깁니다. 상세 주소 등으로 나누어 입력해 주세요.",
  },
  [CartErrorCode.BundlesAndAddonsCannotBeMixed]: {
    en: "Bundle items and separate add-ons cannot be purchased together.",
    jp: "セット商品と追加オプション商品は同時にご購入いただけません。",
    ko: "세트(번들) 상품과 단품 추가 상품(애드온)은 함께 장바구니에 담을 수 없습니다.",
  },
  [CartErrorCode.BuyerCannotPurchaseForCompanyLocation]: {
    en: "Purchasing for this company location is not available.",
    jp: "こちらの企業住所向けのご注文は承ることができません。",
    ko: "선택하신 회사 주소지로는 상품을 구매하실 수 없습니다.",
  },
  [CartErrorCode.CartTooLarge]: {
    en: "Your cart has too many items. Please remove some to proceed.",
    jp: "カート内の商品数が上限を超えています。一部の商品を削除してください。",
    ko: "장바구니에 담긴 상품이 너무 많습니다. 일부 상품을 정리해 주세요.",
  },
  [CartErrorCode.GiftCardRecipientInvalid]: {
    en: "Please check the recipient's information for the gift card.",
    jp: "ギフトカードの受取人情報が正しくありません。再度ご確認ください。",
    ko: "기프트 카드를 받으실 분의 정보가 올바르지 않습니다. 다시 확인해 주세요.",
  },
  [CartErrorCode.Invalid]: {
    en: "Something went wrong with your input. Please check and try again.",
    jp: "入力内容に誤りがあります。もう一度ご確認ください。",
    ko: "입력하신 값이 올바르지 않습니다. 다시 확인해 주세요.",
  },
  [CartErrorCode.InvalidCompanyLocation]: {
    en: "The selected company location is invalid or not allowed.",
    jp: "選択された企業の住所が確認できないか、ご利用いただけません。",
    ko: "등록되지 않았거나 구매가 제한된 회사 주소입니다.",
  },
  [CartErrorCode.InvalidDeliveryAddressId]: {
    en: "The selected delivery address could not be found.",
    jp: "選択された配送先住所が見つかりませんでした。再度ご指定ください。",
    ko: "선택하신 배송지 정보를 찾을 수 없습니다. 다시 선택해 주세요.",
  },
  [CartErrorCode.InvalidDeliveryGroup]: {
    en: "Could not set up delivery for this cart. Please try again.",
    jp: "配送情報の設定中にエラーが発生しました。お手数ですがもう一度お試しください。",
    ko: "배송 정보를 설정하는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
  },
  [CartErrorCode.InvalidDeliveryOption]: {
    en: "The selected delivery option is unavailable. Please choose another.",
    jp: "選択された配送方法はご利用いただけません。別の方法をお選びください。",
    ko: "선택하신 배송 옵션을 이용할 수 없습니다. 다른 배송 방법을 선택해 주세요.",
  },
  [CartErrorCode.InvalidIncrement]: {
    en: "Please adjust the quantity to match the required purchase units.",
    jp: "この商品は指定された販売単位でのみご購入いただけます。",
    ko: "이 상품은 정해진 판매 단위 수량에 맞춰서만 구매할 수 있습니다.",
  },
  [CartErrorCode.InvalidMerchandiseLine]: {
    en: "Could not find the item in your cart. Please refresh and try again.",
    jp: "カート内の商品情報を読み込めませんでした。ページを更新してください。",
    ko: "장바구니의 상품 정보를 찾을 수 없습니다. 페이지를 새로고침해 주세요.",
  },
  [CartErrorCode.InvalidMetafields]: {
    en: "There was a problem with the product configuration.",
    jp: "商品の設定情報に問題が発生しました。カスタマーサポートまでご連絡ください。",
    ko: "상품 옵션 구성에 오류가 발생했습니다. 고객센터로 문의해 주세요.",
  },
  [CartErrorCode.InvalidZipCodeForCountry]: {
    en: "The zip code does not match the selected country.",
    jp: "入力された郵便番号は、選択された国と一致しません。",
    ko: "입력하신 우편번호가 선택하신 국가의 형식과 맞지 않습니다.",
  },
  [CartErrorCode.InvalidZipCodeForProvince]: {
    en: "The zip code does not match the selected region.",
    jp: "入力された郵便番号は、選択された都道府県（州）と一致しません。",
    ko: "입력하신 우편번호가 선택하신 지역(시/도)의 형식과 맞지 않습니다.",
  },
  [CartErrorCode.LessThan]: {
    en: "Please select a quantity within the allowed maximum.",
    jp: "選択可能な最大数を超えています。数量を減らしてください。",
    ko: "구매 가능한 최대 수량을 초과했습니다. 수량을 줄여주세요.",
  },
  [CartErrorCode.MaximumExceeded]: {
    en: "You have exceeded the maximum purchase limit for this item.",
    jp: "この商品はお一人様あたりの最大購入数量を超えています。",
    ko: "이 상품의 최대 구매 가능 수량을 초과했습니다.",
  },
  [CartErrorCode.MerchandiseLineTransformersRunError]: {
    en: "Could not update your cart. Please try again in a moment.",
    jp: "カートの更新中にエラーが発生しました。しばらくしてからもう一度お試しください。",
    ko: "장바구니를 업데이트하는 동안 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
  },
  [CartErrorCode.MerchandiseNotApplicable]: {
    en: "This item cannot be purchased with the current configuration.",
    jp: "選択されたオプションでは、現在この商品をご購入いただけません。",
    ko: "선택하신 옵션으로는 현재 이 상품을 구매하실 수 없습니다.",
  },
  [CartErrorCode.MinimumNotMet]: {
    en: "You must purchase at least the minimum required quantity for this item.",
    jp: "この商品は最低購入数量以上でご注文いただく必要があります。",
    ko: "이 상품을 구매하기 위한 최소 수량을 만족하지 못했습니다.",
  },
  [CartErrorCode.MissingCustomerAccessToken]: {
    en: "Please sign in to your account to use company locations.",
    jp: "企業の住所をご利用いただくには、ログインが必要です。",
    ko: "회사 주소지를 이용하시려면 먼저 로그인을 진행해 주세요.",
  },
  [CartErrorCode.MissingDiscountCode]: {
    en: "Please enter a valid discount code.",
    jp: "ディスカウントコードを入力してください。",
    ko: "할인 코드를 입력해 주세요.",
  },
  [CartErrorCode.MissingNote]: {
    en: "Please enter your message or note.",
    jp: "ご要望（メモ）を入力してください。",
    ko: "요청 사항(메모)을 입력해 주세요.",
  },
  [CartErrorCode.NoteTooLong]: {
    en: "Your note is too long. Please make it shorter.",
    jp: "ご要望（メモ）が長すぎます。お手数ですが、短くまとめて入力してください。",
    ko: "요청 사항이 너무 깁니다. 조금 더 짧게 작성해 주세요.",
  },
  [CartErrorCode.OnlyOneDeliveryAddressCanBeSelected]: {
    en: "You can only select one delivery address per order.",
    jp: "1回のご注文につき、選択できる配送先は1つのみです。",
    ko: "한 번의 주문에는 하나의 배송지만 선택할 수 있습니다.",
  },
  [CartErrorCode.ParentLineInvalidReference]: {
    en: "Unable to add this bundle option. Please refresh and try again.",
    jp: "セット商品の追加に失敗しました。ページを再読み込みしてください。",
    ko: "세트 상품 옵션을 구성하는 중 오류가 발생했습니다. 페이지를 새로고침해 주세요.",
  },
  [CartErrorCode.ParentLineNestingTooDeep]: {
    en: "This bundle configuration is too complex. Please try another option.",
    jp: "セット商品の組み合わせが複雑すぎます。別のオプションをお選びください。",
    ko: "선택하신 세트 상품 조합을 처리할 수 없습니다. 다른 옵션을 선택해 주세요.",
  },
  [CartErrorCode.ParentLineNotFound]: {
    en: "The main item for this bundle could not be found.",
    jp: "セット商品の親アイテムが見つかりませんでした。",
    ko: "해당 세트 상품의 기본 항목을 찾을 수 없습니다.",
  },
  [CartErrorCode.ParentLineOperationBlocked]: {
    en: "This item combination is not compatible. Please check your cart.",
    jp: "組み合わせが正しくないため、カートに追加できませんでした。",
    ko: "함께 구매할 수 없는 상품 조합입니다. 장바구니를 확인해 주세요.",
  },
  [CartErrorCode.PendingDeliveryGroups]: {
    en: "We are fetching your delivery options. Please wait a moment.",
    jp: "配送方法を読み込んでいます。少々お待ちください。",
    ko: "배송 옵션을 확인하고 있습니다. 잠시만 기다려 주세요.",
  },
  [CartErrorCode.ProvinceNotFound]: {
    en: "The selected region could not be found. Please check your address.",
    jp: "選択された都道府県（州）の情報が見つかりませんでした。",
    ko: "선택하신 지역(시/도) 정보를 찾을 수 없습니다. 다시 확인해 주세요.",
  },
  [CartErrorCode.SellingPlanNotApplicable]: {
    en: "The selected purchase option is not available for this item.",
    jp: "選択された定期便・購入プランは、現在この商品に適用できません。",
    ko: "선택하신 정기 배송/구매 옵션을 현재 적용할 수 없습니다.",
  },
  [CartErrorCode.ServiceUnavailable]: {
    en: "Unable to save your cart. Please try again in a few moments.",
    jp: "カートを保存できませんでした。お手数ですが、少し時間をおいて再度お試しください。",
    ko: "일시적인 오류로 장바구니를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.",
  },
  [CartErrorCode.TooManyDeliveryAddresses]: {
    en: "You have added too many addresses. Please remove some to proceed.",
    jp: "登録されている配送先住所が多すぎます。一部を削除してください。",
    ko: "배송지 주소가 너무 많이 설정되어 있습니다. 배송지를 정리해 주세요.",
  },
  [CartErrorCode.UnspecifiedAddressError]: {
    en: "Could not verify the address. Please check your input and try again.",
    jp: "住所を確認できませんでした。入力内容を再度ご確認ください。",
    ko: "주소를 확인하는 동안 오류가 발생했습니다. 다시 확인해 주세요.",
  },
  [CartErrorCode.ValidationCustom]: {
    en: "The information you entered is incorrect. Please check again.",
    jp: "入力された情報が正しくありません。再度ご確認ください。",
    ko: "입력하신 정보가 올바르지 않습니다. 다시 확인해 주세요.",
  },
  [CartErrorCode.VariantRequiresSellingPlan]: {
    en: "This item can only be purchased as a recurring subscription.",
    jp: "この商品は定期便（サブスクリプション）でのみご購入いただけます。",
    ko: "이 상품은 정기 구독(정기 구매) 옵션으로만 구매가 가능합니다.",
  },
  [CartErrorCode.ZipCodeNotSupported]: {
    en: "Delivery is not supported for this zip code.",
    jp: "入力された郵便番号は配送対象外の地域です。",
    ko: "입력하신 우편번호는 배송이 지원되지 않는 지역입니다.",
  },
};

export const cartErrorHandler = (code: Maybe<CartErrorCode> | undefined, lang: "ko" | "en" | "jp"): string => {
  const safeCode = code && CART_ERROR_MESSAGES[code] ? code : "DEFAULT";
  return CART_ERROR_MESSAGES[safeCode][lang];
};
